import { useEffect, useMemo, useState, useCallback } from "react"
import { Box, Center, Checkbox, Collapse, Group, Loader, Stack, Text, TextInput } from "@mantine/core"
import { useTranslation } from "react-i18next"
import { usePermissionsList } from "../hooks/useRoles"
import type { PermissionTreeNode } from "../models/role"

interface Props {
  assignedPermissions: string[]
  onChange: (permissions: Set<string>) => void
}

function collectLeafNames(node: PermissionTreeNode): string[] {
  if (!node.children || node.children.length === 0) {
    return [node.name]
  }
  return node.children.flatMap(collectLeafNames)
}

function collectAllLeafNames(nodes: PermissionTreeNode[]): string[] {
  return nodes.flatMap(collectLeafNames)
}

export default function PermissionTree({ assignedPermissions, onChange }: Props) {
  const { t } = useTranslation()
  const { data: permData, isLoading } = usePermissionsList()

  const initialChecked = useMemo(() => new Set(assignedPermissions), [assignedPermissions])
  const [checked, setChecked] = useState<Set<string>>(initialChecked)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [search, setSearch] = useState("")

  useEffect(() => {
    setChecked(new Set(assignedPermissions))
  }, [assignedPermissions])

  const treeNodes = useMemo(() => permData?.items ?? [], [permData])

  const allLeafNames = useMemo(() => collectAllLeafNames(treeNodes), [treeNodes])

  const handleToggle = useCallback(
    (node: PermissionTreeNode) => {
      const leafNames = collectLeafNames(node)
      const allChecked = leafNames.every((n) => checked.has(n))
      setChecked((prev) => {
        const next = new Set(prev)
        if (allChecked) {
          leafNames.forEach((n) => next.delete(n))
        } else {
          leafNames.forEach((n) => next.add(n))
        }
        onChange(next)
        return next
      })
    },
    [checked, onChange],
  )

  const handleToggleExpand = useCallback((name: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }, [])

  const getNodeState = useCallback(
    (node: PermissionTreeNode): { checked: boolean; indeterminate: boolean } => {
      const leafNames = collectLeafNames(node)
      const checkedCount = leafNames.filter((n) => checked.has(n)).length
      return {
        checked: checkedCount === leafNames.length,
        indeterminate: checkedCount > 0 && checkedCount < leafNames.length,
      }
    },
    [checked],
  )

  const matchesSearch = useCallback(
    (node: PermissionTreeNode): boolean => {
      if (!search) return true
      const q = search.toLowerCase()
      if (node.name.toLowerCase().includes(q)) return true
      if (node.description?.toLowerCase().includes(q)) return true
      if (node.children) {
        return node.children.some(matchesSearch)
      }
      return false
    },
    [search],
  )

  const filteredNodes = useMemo(
    () => (search ? treeNodes.filter(matchesSearch) : treeNodes),
    [treeNodes, search, matchesSearch],
  )

  if (isLoading) {
    return (
      <Center h={400}>
        <Loader />
      </Center>
    )
  }

  if (treeNodes.length === 0) {
    return (
      <Text size="sm" c="dimmed" ta="center" py="xl">
        {t("adminRoles.noPermissions")}
      </Text>
    )
  }

  return (
    <Stack>
      <TextInput
        placeholder={t("adminRoles.searchPermissions")}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        leftSection={<i className="fa fa-search" style={{ fontSize: 14 }} />}
        size="xs"
      />
      <Box
        style={{
          maxHeight: 400,
          overflowY: "auto",
          border: "1px solid var(--mantine-color-gray-3)",
          borderRadius: "var(--mantine-radius-sm)",
          padding: "var(--mantine-spacing-xs)",
        }}
      >
        {filteredNodes.map((node) => (
          <TreeNodeItem
            key={node.name}
            node={node}
            checked={checked}
            expanded={expanded}
            depth={0}
            onToggle={handleToggle}
            onToggleExpand={handleToggleExpand}
            getNodeState={getNodeState}
            matchesSearch={matchesSearch}
            search={search}
          />
        ))}
        {filteredNodes.length === 0 && search && (
          <Text size="xs" c="dimmed" ta="center" py="md">
            {t("adminRoles.noMatchingPermissions")}
          </Text>
        )}
      </Box>
    </Stack>
  )
}

interface TreeNodeItemProps {
  node: PermissionTreeNode
  checked: Set<string>
  expanded: Set<string>
  depth: number
  onToggle: (node: PermissionTreeNode) => void
  onToggleExpand: (name: string) => void
  getNodeState: (node: PermissionTreeNode) => { checked: boolean; indeterminate: boolean }
  matchesSearch: (node: PermissionTreeNode) => boolean
  search: string
}

function TreeNodeItem({
  node,
  checked,
  expanded,
  depth,
  onToggle,
  onToggleExpand,
  getNodeState,
  matchesSearch,
  search,
}: TreeNodeItemProps) {
  const hasChildren = node.children && node.children.length > 0
  const isExpanded = expanded.has(node.name)
  const { checked: isChecked, indeterminate } = getNodeState(node)
  const isLeaf = !hasChildren

  const visibleChildren = useMemo(
    () => (search ? node.children?.filter(matchesSearch) ?? [] : node.children ?? []),
    [node.children, search, matchesSearch],
  )

  if (hasChildren && visibleChildren.length === 0 && search) {
    return null
  }

  return (
    <Box>
      <Group
        gap={4}
        py={4}
        px={4}
        style={{ borderRadius: "var(--mantine-radius-sm)", cursor: isLeaf ? "pointer" : undefined }}
        wrap="nowrap"
      >
        {hasChildren ? (
          <Box
            component="button"
            onClick={() => onToggleExpand(node.name)}
            style={{
              border: "none",
              background: "none",
              cursor: "pointer",
              padding: 0,
              width: 20,
              height: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--mantine-color-gray-6)",
              flexShrink: 0,
            }}
            type="button"
            aria-label={isExpanded ? "Collapse" : "Expand"}
          >
            <i
              className="fa fa-chevron-right"
              style={{
                fontSize: 10,
                transition: "transform 0.15s ease",
                transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
              }}
            />
          </Box>
        ) : (
          <Box style={{ width: 20, flexShrink: 0 }} />
        )}
        <Checkbox
          checked={isChecked}
          indeterminate={indeterminate}
          onChange={() => onToggle(node)}
          size="xs"
          styles={{ label: { fontSize: "var(--mantine-font-size-sm)" } }}
        />
        <Text size="sm" ml={4}>
          {node.name}
          {node.description && (
            <Text component="span" size="xs" c="dimmed" ml="xs">
              — {node.description}
            </Text>
          )}
        </Text>
      </Group>
      {hasChildren && (
        <Collapse in={isExpanded || !!search}>
          <Box pl={depth * 16 + 24}>
            {visibleChildren.map((child) => (
              <TreeNodeItem
                key={child.name}
                node={child}
                checked={checked}
                expanded={expanded}
                depth={depth + 1}
                onToggle={onToggle}
                onToggleExpand={onToggleExpand}
                getNodeState={getNodeState}
                matchesSearch={matchesSearch}
                search={search}
              />
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  )
}
